import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function LocalOrgDetailPage() {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/local-org/by-slug/${slug}`)
      .then(res => { setOrg(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  usePageTitle(org?.name);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 max-w-7xl">
        <Skeleton className="h-10 w-1/2 mb-6" />
        <Skeleton className="h-64 rounded-2xl mb-6" />
        <Skeleton className="h-4 w-full mb-3" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-jci-black/10 mb-4">404</h1>
          <h2 className="text-2xl font-bold text-jci-black mb-2">Organization Not Found</h2>
        </div>
      </div>
    );
  }

  // Parse collage images safely
  let collageImages = [];
  if (org.collage_images) {
    if (typeof org.collage_images === "string") {
      try { 
        collageImages = JSON.parse(org.collage_images); 
      } catch { 
        // If it's a comma-separated string
        collageImages = org.collage_images.split(',').map(img => img.trim()).filter(img => img);
      }
    } else if (Array.isArray(org.collage_images)) {
      collageImages = org.collage_images;
    }
  }

  return (
    <div data-testid="local-org-detail-page">
      {/* Hero with 1:1 image */}
      <div className="bg-jci-black py-16 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex flex-col md:flex-row items-center gap-8">
          {org.hero_image_url && (
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-white/10">
              <img src={org.hero_image_url} alt={org.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{org.name}</h1>
            {org.description && <p className="text-white/60 mt-3 max-w-2xl">{org.description}</p>}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        <Link to="/local-organizations">
          <Button variant="ghost" className="mb-8 text-jci-blue hover:text-jci-navy" data-testid="back-to-orgs">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Local Organizations
          </Button>
        </Link>

        {/* Collage Images */}
        {collageImages.length > 0 && (
          <div data-testid="org-collage">
            <h2 className="text-2xl font-bold text-jci-black mb-6">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {collageImages.map((img, idx) => (
                <button key={idx} onClick={() => setLightbox(img)} className="overflow-hidden rounded-xl group" data-testid={`org-collage-${idx}`}>
                  <img src={img} alt="" className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/90 border-0">
          {lightbox && <img src={lightbox} alt="" className="w-full h-auto max-h-[80vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
